import type {
  AppOption,
  BindingItem,
  MeAuthorization,
  MeLoginLog,
  MeSession,
  MenuNode,
  PageResult,
  PermissionSnapshot,
  QueryParams,
  SecurityOverview,
  UserProfile,
  WechatBind,
} from '@/types/api'
import { del, get, patch, post } from '@/utils/request'

/**
 * 个人中心 / 管理后台的自助接口（附录B §4、附录C §4）。
 * 后端 modules/me 已实现全部端点；页面统一以「接口未就绪 / 失败 / 空」四态区分展示。
 */
export const meApi = {
  profile: () => get<UserProfile>('/me/v1/me'),
  updateProfile: (payload: Partial<UserProfile>) => patch<UserProfile>('/me/v1/me', payload),
  /** 侧边菜单唯一数据源：切换应用必须重新拉取（附录C §2）。应用以 client_id 指定。 */
  menus: (clientId: string) => get<MenuNode[]>('/me/v1/me/menus', { clientId }),
  permissions: (clientId: string) => get<PermissionSnapshot>('/me/v1/me/permissions', { clientId }),
  apps: () => get<AppOption[]>('/me/v1/me/apps'),

  // 安全设置
  security: () => get<SecurityOverview>('/me/v1/me/security'),
  changePassword: (payload: { oldPassword: string; newPassword: string }) =>
    post<void>('/me/v1/me/password', payload),
  bindMobile: (payload: { mobile: string; code: string }) => post<void>('/me/v1/me/mobile', payload),
  bindEmail: (payload: { email: string; code: string }) => post<void>('/me/v1/me/email', payload),
  setMfa: (enabled: boolean) => post<void>('/me/v1/me/mfa', { enabled }),

  // 我的绑定
  bindings: () => get<BindingItem[]>('/me/v1/me/bindings'),
  unbind: (id: string) => del<void>(`/me/v1/me/bindings/${id}`),
  setPrimary: (id: string) => post<void>(`/me/v1/me/bindings/${id}:set-primary`),

  // 已授权应用
  authorizations: () => get<MeAuthorization[]>('/me/v1/me/authorizations'),
  revokeAuthorization: (id: string) => del<void>(`/me/v1/me/authorizations/${id}`),

  // 登录设备
  sessions: () => get<MeSession[]>('/me/v1/me/sessions'),
  kickSession: (id: string) => del<void>(`/me/v1/me/sessions/${id}`),

  // 登录记录
  loginLogs: (params?: QueryParams) => get<PageResult<MeLoginLog>>('/me/v1/me/login-logs', params),

  // 微信绑定
  wechat: () => get<WechatBind[]>('/me/v1/me/wechat'),
  unbindWechat: (id: string) => del<void>(`/me/v1/me/wechat/${id}`),

  // 隐私与数据
  exportData: () => post<{ taskId: string }>('/me/v1/me:export-data'),
  closeAccount: (payload: { reason?: string; verifyCode: string }) =>
    post<void>('/me/v1/me:close-account', payload),
}
